// The caret inside one editable block of the editor (decision 034): offsets in the block's text, its first and last line, a split.

import { domToInline } from './dom-inline.ts';

/** The collapsed caret's offset in the element's text, or null when the selection is elsewhere or spans text. */
export function caretOffset(el: HTMLElement): number | null {
  const sel = getSelection();
  if (!sel || !sel.rangeCount || !sel.isCollapsed || !el.contains(sel.focusNode)) return null;
  const r = document.createRange();
  r.selectNodeContents(el);
  r.setEnd(sel.focusNode!, sel.focusOffset);
  return r.toString().length;
}

export const caretAtStart = (el: HTMLElement) => caretOffset(el) === 0;
export const caretAtEnd = (el: HTMLElement) => caretOffset(el) === (el.textContent ?? '').length;

/** Puts the caret at a text offset of the element (past the end — at the end) and focuses it. */
export function setCaret(el: HTMLElement, at: number | 'end'): void {
  el.focus({ preventScroll: true });
  const sel = getSelection();
  if (!sel) return;
  const r = document.createRange();
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let left = at === 'end' ? Infinity : at;
  let placed = false;
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const len = n.nodeValue?.length ?? 0;
    if (left <= len) { r.setStart(n, left); placed = true; break; }
    left -= len;
  }
  if (!placed) { r.selectNodeContents(el); r.collapse(false); }
  else r.collapse(true);
  sel.removeAllRanges();
  sel.addRange(r);
  el.scrollIntoView?.({ block: 'nearest' });
}

/** The caret's box; an empty line has none, then the element's own. */
function caretRect(el: HTMLElement): DOMRect | null {
  const sel = getSelection();
  if (!sel || !sel.rangeCount || !el.contains(sel.focusNode)) return null;
  const rects = sel.getRangeAt(0).getClientRects();
  return rects.length ? rects[0]! : null;
}

/** Whether the caret stands on the first (or the last) visual line of the element: arrows then leave the block. */
export function onEdgeLine(el: HTMLElement, edge: 'first' | 'last'): boolean {
  const c = caretRect(el);
  if (!c) return true;
  const box = el.getBoundingClientRect();
  const line = parseFloat(getComputedStyle(el).lineHeight) || 20;
  return edge === 'first' ? c.top - box.top < line * 0.8 : box.bottom - c.bottom < line * 0.8;
}

/** Where the caret is on the screen, for the «/» menu. */
export function caretPoint(el: HTMLElement): { x: number; y: number } {
  const c = caretRect(el);
  const box = el.getBoundingClientRect();
  return c && (c.width || c.height) ? { x: c.left, y: c.bottom } : { x: box.left, y: box.bottom };
}

/** Cuts the element's content after the caret off: the element keeps what is before, the inline markdown of both is returned. */
export function splitAtCaret(el: HTMLElement): { before: string; after: string } {
  const sel = getSelection();
  if (!sel || !sel.rangeCount || !el.contains(sel.focusNode)) return { before: domToInline(el), after: '' };
  const r = sel.getRangeAt(0).cloneRange();
  r.deleteContents();
  const tail = document.createRange();
  tail.setStart(r.startContainer, r.startOffset);
  tail.setEnd(el, el.childNodes.length);
  const box = document.createElement('div');
  box.appendChild(tail.extractContents());
  return { before: domToInline(el), after: domToInline(box) };
}

/** Removes `count` characters of the element's text starting at `from` (the «/команда» or a «## » shortcut that did its job). */
export function deleteText(el: HTMLElement, from: number, count: number): void {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const r = document.createRange();
  let pos = 0, started = false;
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const len = n.nodeValue?.length ?? 0;
    if (!started && from <= pos + len) { r.setStart(n, from - pos); started = true; }
    if (started && from + count <= pos + len) { r.setEnd(n, from + count - pos); r.deleteContents(); return; }
    pos += len;
  }
}
